import { useCallback, useEffect, useState } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { AREA_LABEL, GOAL_STATUS_LABEL, fetchGoals, type Area, type Goal, type GoalStatus } from './api';
import GoalCard, { type AskConfirm } from './GoalCard';
import GoalEditor from './GoalEditor';
import TemplatePicker from './TemplatePicker';
import './goals.css';

type Pending = Parameters<AskConfirm>[0];

export default function GoalsBoard() {
  const t = useT();
  const { learnerId } = useLearner();
  const [goals, setGoals] = useState<Goal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [picking, setPicking] = useState(false);
  const [area, setArea] = useState<Area | ''>('');
  const [status, setStatus] = useState<GoalStatus | ''>('');
  const [waiting, setWaiting] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!learnerId) return;
    fetchGoals(learnerId)
      .then((list) => { setGoals(list); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : t('The goals could not be loaded.')));
  }, [learnerId, t]);

  useEffect(() => {
    setGoals(null);
    setAdding(false);
    setPicking(false);
    load();
  }, [load]);

  const cancel = useCallback(() => { if (!busy) setPending(null); }, [busy]);

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    try {
      await pending.run();
      setPending(null);
      load();
    } catch (e) {
      setPending(null);
      setError(e instanceof Error ? e.message : t('That could not be deleted just now.'));
    } finally {
      setBusy(false);
    }
  }

  if (!learnerId) return <p className="muted">{t('Choose a learner to see their goals.')}</p>;

  const shown = (goals ?? []).filter((g) =>
    (!area || g.area === area)
    && (!status || g.status === status)
    && (!waiting || g.openComments > 0 || g.targets.some((x) => x.openComments > 0)));
  const areas = (Object.keys(AREA_LABEL) as Area[]).filter((a) => shown.some((g) => g.area === a));
  const editor = adding && (
    <div className="card planner-goal">
      <GoalEditor learnerId={learnerId} goal={null} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
    </div>
  );

  return (
    <div className="planner-goal-board">
      <p className="planner-goal-intro">
        {t('Goals are what you want for your learner over the year. Targets are the steps towards them, reviewed each term. Activities are how a target is taught.')}
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      {goals === null && !error && <p className="muted" role="status">{t('Loading goals…')}</p>}

      {goals && goals.length === 0 && (
        adding ? editor : (
          <div className="card planner-goal-empty">
            <h2>{t('No goals yet')}</h2>
            <p>{t('Start with one thing you would like to be different for your learner by the end of the year. You can add the smaller steps, and how to teach them, afterwards.')}</p>
            <div className="toolbar">
              <button type="button" className="btn" onClick={() => setAdding(true)}>{t('Add your first goal')}</button>
              <button type="button" className="btn secondary" onClick={() => setPicking(true)}>{t('Start from a template')}</button>
            </div>
          </div>
        )
      )}

      {goals && goals.length > 0 && (
        <>
          <div className="planner-goal-filters">
            <div className="field">
              <label htmlFor="goal-filter-area">{t('Area of need')}</label>
              <select id="goal-filter-area" value={area} onChange={(e) => setArea(e.target.value as Area | '')}>
                <option value="">{t('All areas')}</option>
                {(Object.keys(AREA_LABEL) as Area[]).map((a) => <option key={a} value={a}>{t(AREA_LABEL[a])}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="goal-filter-status">{t('Status')}</label>
              <select id="goal-filter-status" value={status} onChange={(e) => setStatus(e.target.value as GoalStatus | '')}>
                <option value="">{t('Any status')}</option>
                {(Object.keys(GOAL_STATUS_LABEL) as GoalStatus[]).map((s) => <option key={s} value={s}>{t(GOAL_STATUS_LABEL[s])}</option>)}
              </select>
            </div>
            <label className="check planner-goal-filter-check">
              <input type="checkbox" checked={waiting} onChange={(e) => setWaiting(e.target.checked)} />
              <span>{t('Only with questions waiting')}</span>
            </label>
            <button type="button" className="btn" onClick={() => setAdding(true)} disabled={adding}>{t('Add a goal')}</button>
            <button type="button" className="btn secondary" onClick={() => setPicking(true)}>{t('Start from a template')}</button>
          </div>
          {editor}
          {shown.length === 0 && <p className="muted">{t('No goals match these filters.')}</p>}
          {areas.map((a) => (
            <section key={a} className="planner-goal-area" aria-labelledby={`goal-area-${a}`}>
              <h2 id={`goal-area-${a}`}>{t(AREA_LABEL[a])}</h2>
              {shown.filter((g) => g.area === a).map((g) => <GoalCard key={g.id} learnerId={learnerId} goal={g} onChanged={load} ask={setPending} />)}
            </section>
          ))}
        </>
      )}

      {picking && <TemplatePicker learnerId={learnerId} onClose={() => setPicking(false)} onAdded={() => { setPicking(false); load(); }} />}

      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ''}
        message={pending?.message ?? ''}
        confirmLabel={busy ? t('Deleting…') : pending?.confirmLabel}
        destructive
        onConfirm={() => { if (!busy) void confirm(); }}
        onCancel={cancel}
      />
    </div>
  );
}
